import { useEffect, useState } from "react";
import { getHolidayCalendar, getHolidays, getEmployeeCompOffs } from "../../api/holidayApi";
import Loader from "../../components/Loader";
import { API_BASE_URL } from "../../api/axios";
import { formatDateDMY } from "../../utils/dateFormatter";

const HolidayCalendar = () => {
  const [calendar, setCalendar] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [compOffGrants, setCompOffGrants] = useState([]);
  const [compOffBalance, setCompOffBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getHolidayCalendar(), getHolidays(), getEmployeeCompOffs()])
      .then(([calendarRes, holidayRes, compOffRes]) => {
        setCalendar(calendarRes.data);
        setHolidays(holidayRes.data);
        setCompOffGrants(compOffRes.data.grants || []);
        setCompOffBalance(compOffRes.data.available_balance || 0);
      })
      .catch((err) => {
        console.error("Failed to load holiday/compoff data:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const formatDateWithHyphens = (dateStr) => {
    if (!dateStr) return "-";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    }
    return dateStr;
  };

  if (loading) return <Loader />;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Holidays</h2>

      {/* CALENDAR FILE */}
      <div className="bg-white p-6 shadow rounded mb-6">
        <h3 className="font-semibold mb-2">Holiday Calendar</h3>

        {calendar?.file ? (
          <a
            href={calendar.file.startsWith("http") ? calendar.file : `${API_BASE_URL}${calendar.file}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            View Holiday Calendar
          </a>
        ) : (
          <p className="text-gray-500">No holiday calendar uploaded</p>
        )}
      </div>

      {/* MANUAL HOLIDAYS */}
      <div className="bg-white p-6 shadow rounded">
        <h3 className="font-semibold mb-4">Additional Holidays</h3>

        {holidays.length === 0 ? (
          <p className="text-gray-500">No holidays announced</p>
        ) : (
          <table className="min-w-full text-sm text-center">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Holiday</th>
                <th className="px-4 py-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id} className="border-t">
                  <td className="px-4 py-2">{formatDateDMY(h.date)}</td>
                  <td className="px-4 py-2">{h.name}</td>
                  <td className="px-4 py-2">{h.description || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* COMP OFF SECTION */}
      <div className="bg-white p-6 shadow rounded mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg text-gray-800">Compensatory Offs (Comp Off)</h3>
          <div className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1.5 rounded-full">
            Available Balance: {compOffBalance} {compOffBalance === 1 ? "Day" : "Days"}
          </div>
        </div>

        {compOffGrants.length === 0 ? (
          <p className="text-gray-500">No compensatory offs granted</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-center">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3">Comp Off Date</th>
                  <th className="px-4 py-3">Granted Days</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Granted By</th>
                  <th className="px-4 py-3">Granted On</th>
                </tr>
              </thead>
              <tbody className="divide-y text-gray-600">
                {compOffGrants.map((grant) => (
                  <tr key={grant.id} className="border-t hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {formatDateWithHyphens(grant.comp_off_date)}
                    </td>
                    <td className="px-4 py-3">{grant.days}</td>
                    <td className="px-4 py-3">{grant.reason || "-"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          grant.status === "Active"
                            ? "bg-green-100 text-green-700"
                            : grant.status === "Partially Used"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {grant.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{grant.granted_by}</td>
                    <td className="px-4 py-3">
                      {formatDateWithHyphens(grant.granted_on)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HolidayCalendar;
